import React from "react";
import { Mail, Phone, MapPin, Clock } from "lucide-react";
import ContactForm from "../components/ContactForm";
import { BRAND } from "../data/content";
import { PAGE_SEO, SITE } from "../data/seoContent";
import SEO, { breadcrumbSchema } from "../components/SEO";
import Breadcrumb from "../components/Breadcrumb";

const contactSchema = {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  name: "Contact Oxford Environmental Consultants",
  url: `${SITE.domain}/contact`,
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+44-3456-868-868",
    email: "enquiries@oxford-ec.co.uk",
    contactType: "customer service",
    areaServed: "GB",
    availableLanguage: "en",
  },
};

export default function Contact() {
  const crumbs = [{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }];
  return (
    <div data-testid="page-contact">
      <SEO {...PAGE_SEO.contact} schema={[breadcrumbSchema(crumbs), contactSchema]} />
      <section className="bg-[#0A0F1C] text-white pt-16 pb-20 lg:pt-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="mb-8"><Breadcrumb items={crumbs} theme="dark" /></div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-6">Contact</p>
          <h1 className="font-display text-5xl lg:text-7xl font-bold tracking-tight max-w-4xl leading-[1.05]">
            Let's talk <span className="gs-gradient-text">compliance.</span>
          </h1>
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12">
          <aside className="lg:col-span-4 bg-[#0A0F1C] text-white p-10 lg:p-16">
            <p className="text-[10px] uppercase tracking-[0.25em] text-white/50 mb-6">Get in touch</p>
            <h2 className="font-display text-3xl font-bold mb-10 leading-tight">Scope, schedule and sign-off — one call away.</h2>
            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 border border-white/15 flex items-center justify-center flex-shrink-0"><Phone className="w-4 h-4" /></div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-white/50 mb-1">Phone</p>
                  <a href={`tel:${BRAND.phone.replace(/\s/g,"")}`} className="text-white font-semibold text-lg hover:gs-gradient-text">{BRAND.phone}</a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 border border-white/15 flex items-center justify-center flex-shrink-0"><Mail className="w-4 h-4" /></div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-white/50 mb-1">Email</p>
                  <a href={`mailto:${BRAND.email}`} className="text-white font-semibold hover:gs-gradient-text">{BRAND.email}</a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 border border-white/15 flex items-center justify-center flex-shrink-0"><MapPin className="w-4 h-4" /></div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-white/50 mb-1">Coverage</p>
                  <p className="text-white">United Kingdom — national</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 border border-white/15 flex items-center justify-center flex-shrink-0"><Clock className="w-4 h-4" /></div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-white/50 mb-1">Hours</p>
                  <p className="text-white">Mon–Fri · 08:00–18:00</p>
                  <p className="text-white/60 text-sm mt-1">Out-of-hours: on-call rota</p>
                </div>
              </div>
            </div>
          </aside>

          <div className="lg:col-span-8 p-10 lg:p-16">
            <p className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-4">Submit an enquiry</p>
            <h2 className="font-display text-3xl font-bold text-slate-900 mb-8 tracking-tight">A consultant will respond within one working day.</h2>
            <ContactForm />
          </div>
        </div>
      </section>
    </div>
  );
}
